import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import AppLayout from "./AppLayout";
import DefaultLayout from "./Default";
import PatientFormLayout from "./PreRegistrationLayout";
import KioskPatientFormLayout from "./PreRegistrationLayout/KioskPatientFormLayout";
import NewAppLayout from "./NewAppLayout";
import { isKioskModeActive } from "../../utils/patientFormKiosk";
import { useSelector, useDispatch } from "react-redux";
import { setIsNewUI } from "../../store/CommonSlice";
 
const Layouts = ({ type, children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isNewUI = useSelector((state) => state.common.isNewUI);
  const [loadingTheme, setLoadingTheme] = useState(true);
  const isMountedRef = useRef(true);
  const themeEffectRunIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ Initialize theme from localStorage
  // useEffect(() => {
  //   try {
  //     const saved = localStorage.getItem("isNewUI");
  //     if (saved !== null) {
  //       dispatch(setIsNewUI(JSON.parse(saved)));
  //     } else {
  //       dispatch(setIsNewUI(true));
  //       try {
  //         localStorage.setItem("isNewUI", JSON.stringify(true));
  //       } catch (e) {
  //         // localStorage might be disabled in incognito mode
  //         console.warn("localStorage not available:", e);
  //       }
  //     }
  //   } catch (e) {
  //     // Fallback if localStorage fails
  //     console.warn("Failed to read from localStorage:", e);
  //     dispatch(setIsNewUI(true));
  //   }
  // }, [dispatch]);

  // Set New theme always
  useEffect(() => {
    dispatch(setIsNewUI(true));

    try {
      localStorage.removeItem("isNewUI"); // remove old stored value permanently
    } catch (e) {
      console.warn("localStorage cleanup failed:", e);
    }
  }, [dispatch]);

  // Check if CSS files are already loaded in DOM (and verified via styleSheets)
  const checkCssLoaded = (prefix, files) => {
    try {
      // Must be able to verify via document.styleSheets - never assume "element exists" = loaded.
      // After login redirect, previous route's link tags can remain in head but not yet applied;
      // in incognito we often can't read styleSheets, so we must not short-circuit and must re-load.
      return files.every((_, index) => {
        const id = `${prefix}${index}`;
        const linkElement = document.getElementById(id);
        if (!linkElement || !linkElement.href || linkElement.rel !== "stylesheet") {
          return false;
        }
        try {
          const stylesheet = Array.from(document.styleSheets).find(
            (sheet) => sheet.ownerNode === linkElement
          );
          return stylesheet !== undefined;
        } catch (e) {
          // Incognito/cross-origin: cannot verify - do NOT assume loaded (was causing no-CSS on first login)
          return false;
        }
      });
    } catch (e) {
      return false;
    }
  };

  // ✅ Dynamically load the correct CSS bundle
  useEffect(() => {
    themeEffectRunIdRef.current += 1;
    const currentRunId = themeEffectRunIdRef.current;

    // Safe localStorage access
    try {
      localStorage.setItem("isNewUI", JSON.stringify(isNewUI));
    } catch (e) {
      // localStorage might be disabled in incognito mode
      console.warn("localStorage not available:", e);
    }

    const newPrefix = "dynamic-new-css-";
    const oldPrefix = "dynamic-old-css-";

    const newCssFiles = [
      "newbootstrap_main.css",
      "newbootstrap.css",
      "newmain.css",
      "newresponsive.css",
      "newfullcalendar.css",
      "newforms.css",
    ];

    const oldCssFiles = [
      "sb-admin-2.css",
      "common.css",
      "sb_table.css",
      "sh_forms.css",
      "sh_colors.css",
      "sh_wizards.css",
      "sh_fullcalendar.css",
      "responsive.css",
      "dark.css",
    ];

    const loadCss = (prefix, files, basePath) => {
      const promises = [];

      files.forEach((file, index) => {
        const id = `${prefix}${index}`;
        const existingLink = document.getElementById(id);
        if (existingLink) {
          try {
            const stylesheet = Array.from(document.styleSheets).find(
              (sheet) => sheet.ownerNode === existingLink
            );
            if (stylesheet) {
              promises.push(Promise.resolve(true));
              return;
            }
          } catch (e) {
            // Incognito/cross-origin: can't verify - remove stale link and load fresh so onload fires
          }
          existingLink.remove();
        }

        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = `${basePath}/${file}?v=${process.env.REACT_APP_PACKAGE_VERSION}`;

        const promise = new Promise((resolve) => {
          const timeout = setTimeout(() => {
            // Timeout after 10 seconds per file
            console.warn(`CSS file ${file} loading timeout`);
            resolve(false);
          }, 10000);

          link.onload = () => {
            clearTimeout(timeout);
            resolve(true);
          };
          link.onerror = () => {
            clearTimeout(timeout);
            console.warn(`Failed to load CSS file: ${file}`);
            resolve(false);
          };
        });

        try {
          document.head.appendChild(link);
          promises.push(promise);
        } catch (e) {
          console.error(`Error appending CSS link for ${file}:`, e);
          promises.push(Promise.resolve(false));
        }
      });

      return Promise.all(promises);
    };

    const removeCss = (prefix, files) => {
      files.forEach((_, index) => {
        const el = document.getElementById(`${prefix}${index}`);
        if (el) el.remove();
      });
    };

    // Only the latest effect run may finish; prevents stale run from hiding overlay too early (e.g. incognito isNewUI flip)
    const safeFinishLoading = () => {
      if (currentRunId !== themeEffectRunIdRef.current || !isMountedRef.current) return;
      setLoadingTheme(false);
    };

    // ✅ Check if CSS is already loaded (e.g., on page refresh)
    let cssAlreadyLoaded = false;
    if (isNewUI) {
      const isNewCssLoaded = checkCssLoaded(newPrefix, newCssFiles);
      if (isNewCssLoaded) {
        removeCss(oldPrefix, oldCssFiles);
        safeFinishLoading();
        cssAlreadyLoaded = true;
      }
    } else {
      const isOldCssLoaded = checkCssLoaded(oldPrefix, oldCssFiles);
      if (isOldCssLoaded) {
        removeCss(newPrefix, newCssFiles);
        safeFinishLoading();
        cssAlreadyLoaded = true;
      }
    }

    if (cssAlreadyLoaded) {
      return;
    }

    setLoadingTheme(true);

    const maxTimeout = setTimeout(() => {
      console.warn("CSS loading timeout - showing content anyway");
      safeFinishLoading();
    }, 30000);

    const loadTheme = isNewUI
      ? loadCss(
          newPrefix,
          newCssFiles,
          `${process.env.PUBLIC_URL}/assets/New_theme_css`
        )
      : loadCss(
          oldPrefix,
          oldCssFiles,
          `${process.env.PUBLIC_URL}/assets/css`
        );

    loadTheme
      .then(() => {
        clearTimeout(maxTimeout);
        if (currentRunId !== themeEffectRunIdRef.current) return;
        if (isNewUI) {
          removeCss(oldPrefix, oldCssFiles);
        } else {
          removeCss(newPrefix, newCssFiles);
        }
        // Short delay so browser can apply styles (fixes incognito first-load showing unstyled content)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            safeFinishLoading();
          });
        });
      })
      .catch((error) => {
        clearTimeout(maxTimeout);
        console.error("Error loading CSS:", error);
        safeFinishLoading();
      });

    // Cleanup function
    return () => {
      clearTimeout(maxTimeout);
    };
  }, [isNewUI]);
 
  // ✅ Choose layout type
  const HOC = isNewUI ? NewAppLayout : AppLayout;
 
  // ✅ Optional loading overlay (prevents flicker)
const LoadingOverlay = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      transition: "opacity 0.4s ease",
    }}
  >
    <div
      style={{
        width: "50px",
        height: "50px",
        border: "5px solid #ccc",
        borderTopColor: "#007bff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    ></div>
    <p
      style={{
        marginTop: "16px",
        fontSize: "1rem",
        fontWeight: "500",
        color: "#333",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      Switching to {isNewUI ? "New Experience ✨" : "Classic Mode 🕰️"}...
    </p>
 
    <style>
      {`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);
 
 
  if (loadingTheme) {
    return <LoadingOverlay />;
  }

  switch (type) {
    case "App":
      return <HOC>{children}</HOC>;
    case "Kiosk Patient Form":
      return isKioskModeActive(location) ? (
        <KioskPatientFormLayout>{children}</KioskPatientFormLayout>
      ) : (
        <PatientFormLayout>{children}</PatientFormLayout>
      );
    case "Patient Form":
      return <PatientFormLayout>{children}</PatientFormLayout>;
    case "Terms":
      return <>{children}</>;
    default:
      return <DefaultLayout>{children}</DefaultLayout>;
  }
};
 
export default Layouts;
 